import type { EmbedProviderType } from "../../../../common/interfaces/admin/integration.interface";

/**
 * Tenant-scoped admin test sign-in for FR-11 (test-before-activate).
 *
 * The admin app runs on per-company subdomains (`<company>.dev.kenbakke.com`),
 * which can't all be Firebase-authorized OAuth domains. So instead of running
 * `signInWithPopup` here, we open the embed host's `/sso-test` page (on
 * `chat.<env>`, an authorized domain) in a popup. That page performs the
 * tenant-scoped SAML/OIDC sign-in and `postMessage`s the resulting Firebase ID
 * token back. We then hand that token to `POST /:id/test-sso`, which
 * re-verifies it tenant-scoped and records pass/fail. No session is minted.
 */
interface SsoTestMessage {
    source: "mbassist-sso-test";
    ok: boolean;
    idToken?: string;
    error?: string;
}

export async function signInForSsoTest(args: {
    /** Origin of the embed host, e.g. https://chat.dev.kenbakke.com. */
    chatOrigin: string;
    tenantId: string;
    providerId: string;
    providerType: EmbedProviderType;
}): Promise<string> {
    const url = new URL("/sso-test", args.chatOrigin);
    url.searchParams.set("tenant", args.tenantId);
    url.searchParams.set("provider", args.providerId);
    url.searchParams.set("type", args.providerType);
    url.searchParams.set("origin", window.location.origin);

    // Open synchronously within the click gesture or the browser blocks it.
    const popup = window.open(url.toString(), "mbassist-sso-test", "width=520,height=680");
    if (!popup) {
        // Mirror the Firebase error shape so the caller's message map still works.
        throw { code: "auth/popup-blocked" };
    }

    return new Promise<string>((resolve, reject) => {
        let settled = false;

        const cleanup = () => {
            settled = true;
            window.removeEventListener("message", onMessage);
            window.clearInterval(poll);
            try {
                popup.close();
            } catch {
                /* already closed */
            }
        };

        const onMessage = (ev: MessageEvent) => {
            // Only accept messages from the embed host page we opened.
            if (ev.origin !== args.chatOrigin) return;
            const data = ev.data as SsoTestMessage | undefined;
            if (!data || data.source !== "mbassist-sso-test") return;
            if (settled) return;
            cleanup();
            if (data.ok && data.idToken) {
                resolve(data.idToken);
            } else {
                reject(new Error(data.error || "SSO test sign-in failed."));
            }
        };

        // If the user closes the popup before it reports a result, fail cleanly.
        const poll = window.setInterval(() => {
            if (popup.closed && !settled) {
                cleanup();
                reject(new Error("Sign-in window was closed before completing."));
            }
        }, 500);

        window.addEventListener("message", onMessage);
    });
}
